import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Check, ChevronDown, Play } from "lucide-react";
import Avatar from "./Avatar";

const PRIORITY_DOT = {
    high: "bg-terracotta",
    medium: "bg-mustard",
    low: "bg-sage",
};

export default function CheckInPopup({
    open,
    onDismiss,
    message,
    mood,
    avatarStyle = "feminine",
    kind,             // 'reminder' | 'accountability' | undefined
    title,            // reminder title
    onMarkDone,       // optional handler for reminders
    onSnooze,         // optional handler for accountability pings → "Snooze 30m"
    onPickTask,       // optional (taskId) => void for accountability — swaps the popup's task
    onStartFocusTask, // optional (task) => void — kick off a 25/5 Pomodoro from the picker
    pickerTasks = [], // undone tasks excluding the current one (for the picker)
    nervousness = 0,  // 0 calm, 1 worried, 2 frustrated
}) {
    const isReminder = kind === "reminder";
    const borderTone = nervousness >= 2 ? "border-terracotta" : nervousness >= 1 ? "border-mustard" : "border-mustard";
    const shakeMagnitude = nervousness >= 2 ? 6 : nervousness >= 1 ? 3 : 0;
    const [pickerOpen, setPickerOpen] = useState(false);
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="popup"
                    initial={{ y: isReminder ? -80 : 80, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: isReminder ? -40 : 40, opacity: 0, scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 280, damping: 22 }}
                    className={`fixed left-3 right-3 z-[60] ${
                        isReminder
                            ? "sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md"
                            : "sm:right-6 sm:left-auto sm:max-w-sm"
                    }`}
                    style={
                        isReminder
                            ? { top: "max(env(safe-area-inset-top), 16px)" }
                            : { bottom: "calc(max(env(safe-area-inset-bottom), 0px) + 132px)" }
                    }
                    data-testid="checkin-popup"
                >
                    <motion.div
                        animate={isReminder ? {
                            x: shakeMagnitude
                                ? [0, -shakeMagnitude, shakeMagnitude, -shakeMagnitude, shakeMagnitude, 0]
                                : 0,
                            boxShadow: [
                                "0 16px 40px rgba(210,131,105,0.18)",
                                nervousness >= 2
                                    ? "0 18px 56px rgba(168,92,79,0.55)"
                                    : "0 16px 56px rgba(229,185,107,0.45)",
                                "0 16px 40px rgba(210,131,105,0.18)",
                            ],
                        } : undefined}
                        transition={isReminder ? {
                            duration: nervousness >= 2 ? 0.45 : nervousness >= 1 ? 0.9 : 1.4,
                            repeat: nervousness > 0 ? Infinity : 3,
                        } : undefined}
                        className={`bg-cream rounded-clay p-4 flex items-center gap-3 border-2 ${
                            isReminder ? borderTone : "border-warm-300/40 shadow-float"
                        }`}
                    >
                        <div className="shrink-0 relative">
                            {isReminder && (
                                <motion.span
                                    animate={{ rotate: [-12, 12, -12, 12, 0] }}
                                    transition={{ duration: 0.8, repeat: 2, ease: "easeInOut" }}
                                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-mustard text-warm-900 flex items-center justify-center shadow-clay z-10"
                                >
                                    <Bell size={14} strokeWidth={2.5} />
                                </motion.span>
                            )}
                            <Avatar mood={mood || "happy"} size={64} style={avatarStyle} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-[10px] uppercase tracking-widest text-warm-500">
                                {isReminder
                                    ? (nervousness >= 2 ? "Reminder · Mochi (worried!)" : nervousness >= 1 ? "Reminder · Mochi (nervous)" : "Reminder · Mochi")
                                    : "Mochi"}
                            </p>
                            {isReminder && title && (
                                <p className="font-display text-warm-900 text-base leading-snug truncate" data-testid="reminder-title">
                                    {title}
                                </p>
                            )}
                            <p className="font-body text-warm-800 text-sm leading-snug mt-0.5">{message}</p>
                        </div>
                        <div className="shrink-0 flex flex-col gap-1.5">
                            {isReminder && onMarkDone && (
                                <button
                                    onClick={onMarkDone}
                                    className="bg-sage hover:bg-sage-hover text-cream font-display text-xs px-3 py-2 rounded-full inline-flex items-center gap-1 transition-all active:scale-95"
                                    data-testid="reminder-done"
                                >
                                    <Check size={12} strokeWidth={3} /> Done
                                </button>
                            )}
                            {onPickTask && pickerTasks.length > 0 && (
                                <div className="relative">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setPickerOpen((v) => !v); }}
                                        className="bg-sage-soft hover:bg-sage/30 text-warm-900 font-display text-xs px-3 py-2 rounded-full inline-flex items-center gap-1 transition-all active:scale-95"
                                        data-testid="checkin-pick-different"
                                        title="Pick a different task to focus on"
                                    >
                                        Different task
                                        <ChevronDown size={11} className={`transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    <AnimatePresence>
                                        {pickerOpen && (
                                            <motion.div
                                                key="task-picker"
                                                initial={{ opacity: 0, y: 6, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 6, scale: 0.95 }}
                                                transition={{ duration: 0.15 }}
                                                className="absolute right-0 bottom-full mb-2 w-56 max-h-56 overflow-y-auto bg-cream rounded-2xl shadow-float border border-warm-300/40 py-1 z-50"
                                                data-testid="checkin-task-picker"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <p className="font-display text-[10px] uppercase tracking-widest text-warm-500 px-3 pt-1.5 pb-1">
                                                    Pick a task
                                                </p>
                                                {pickerTasks.map((t) => (
                                                    <div
                                                        key={t.id}
                                                        className="flex items-center gap-1 px-3 py-2 hover:bg-mustard-soft/40 transition-colors"
                                                    >
                                                        <button
                                                            onClick={() => { setPickerOpen(false); onPickTask(t.id); }}
                                                            className="flex-1 flex items-center gap-2 text-left font-body text-sm text-warm-900"
                                                            data-testid={`checkin-task-option-${t.id}`}
                                                        >
                                                            <span
                                                                className={`shrink-0 w-2 h-2 rounded-full ${PRIORITY_DOT[t.priority] || "bg-warm-300"}`}
                                                                aria-hidden="true"
                                                            />
                                                            <span className="truncate flex-1">{t.title}</span>
                                                        </button>
                                                        {onStartFocusTask && (
                                                            <button
                                                                onClick={() => { setPickerOpen(false); onStartFocusTask(t); }}
                                                                className="shrink-0 p-1.5 rounded-full text-terracotta hover:bg-terracotta-soft/50 transition-colors"
                                                                data-testid={`checkin-task-focus-${t.id}`}
                                                                title="Start a 25-min focus session on this task"
                                                                aria-label={`Start focus on ${t.title}`}
                                                            >
                                                                <Play size={12} fill="currentColor" />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                    {pickerOpen && (
                                        <div
                                            className="fixed inset-0 z-40"
                                            onClick={() => setPickerOpen(false)}
                                            aria-hidden="true"
                                        />
                                    )}
                                </div>
                            )}
                            {onSnooze && (
                                <button
                                    onClick={onSnooze}
                                    className="bg-mustard-soft hover:bg-mustard text-warm-900 font-display text-xs px-3 py-2 rounded-full transition-all active:scale-95"
                                    data-testid="checkin-snooze"
                                    title="Pause Mochi's nudges for 30 minutes"
                                >
                                    Snooze 30m
                                </button>
                            )}
                            <button
                                onClick={onDismiss}
                                className="bg-terracotta hover:bg-terracotta-hover text-cream font-display text-xs px-3 py-2 rounded-full transition-all active:scale-95"
                                data-testid="checkin-dismiss"
                            >
                                {isReminder ? "Snooze" : "Got it"}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
